import type { BigNumber } from "@yjsgames/bignumber";
import { createDefaultProjectileSpec, type ProjectileSpec } from "../models/ProjectileSpec.js";
import { ALWAYS_PROC, type ModuleProcKind } from "../modules/ModuleEffect.js";
import type { ProjectileModule } from "../modules/ProjectileModule.js";

/**
 * 기본 스펙에 모듈을 **주어진 순서대로** 누적 apply해 최종 스펙을 만든다 (지시문 §10·§13).
 * 순서 = Start→Fire 연결 경로 순서(ModulePathFinder 결과). 순수 로직 — 단위 테스트 대상.
 * rng는 각 모듈 effect의 chance 게이트 판정용(발사 1회 단위). 미지정 시 항상 발동(표시/QA 결정론).
 * onProc(index, kind)는 modules[index]가 chance 게이트 발동에 성공했을 때 호출(시각 연출 트리거). 호출자가 index→instanceId 매핑.
 *
 * echo*Chances[i] = i번째 모듈(잔향/전조)이 이전(i-1)/다음(i+1) 모듈 효과를 한 번 더 발동시킬 확률(없으면 0).
 * 발동 시 이웃은 rng 미주입 재apply(확정 재발동 — 이웃 chance 게이트 이중판정 회피, pipeCrit는 미확정). 재apply는 단순 apply라
 * echo를 재트리거하지 않아 1회 한정(무한재귀 없음). 경계·fused 이웃 제외는 호출자(CombatSystem)가 확률 0으로 처리.
 */
export class ProjectileSpecBuilder {
  build(
    baseDamage: BigNumber,
    modules: readonly ProjectileModule[],
    rng?: () => number,
    onProc?: (index: number, kind: ModuleProcKind) => void,
    chanceBonuses?: readonly number[],
    echoPrevChances?: readonly number[],
    echoNextChances?: readonly number[],
  ): ProjectileSpec {
    let spec = createDefaultProjectileSpec(baseDamage);
    const gateRng = rng ?? ALWAYS_PROC;
    const last = modules.length - 1;
    modules.forEach((m, i) => {
      // chanceBonuses[i] = 파이프라인 강화(정밀)가 i번째 모듈에 준 발동확률 가산(없으면 0).
      spec = m.apply(spec, rng, onProc ? (kind) => onProc(i, kind) : undefined, chanceBonuses?.[i] ?? 0);
      // 잔향: 이전(i-1) 모듈 효과 재발동. 다음 줄 rng() 소비는 echo 게이트 판정(정상 apply 게이트와 별개).
      const prevP = echoPrevChances?.[i] ?? 0;
      if (prevP > 0 && i > 0 && gateRng() < prevP) {
        spec = modules[i - 1]!.apply(
          spec,
          undefined,
          onProc ? (kind) => onProc(i - 1, kind) : undefined,
          chanceBonuses?.[i - 1] ?? 0,
        );
      }
      // 전조: 다음(i+1) 모듈 효과 재발동. 루프 순서상 i=k에서 i+1(선발동) → i=k+1에서 정규 apply = 합계 2회.
      // 이는 잔향(정규→재발동)과 대칭: 두 경우 모두 이웃 효과가 "1번 더" 발동되는 기획 의도를 충족한다.
      const nextP = echoNextChances?.[i] ?? 0;
      if (nextP > 0 && i < last && gateRng() < nextP) {
        spec = modules[i + 1]!.apply(
          spec,
          undefined,
          onProc ? (kind) => onProc(i + 1, kind) : undefined,
          chanceBonuses?.[i + 1] ?? 0,
        );
      }
    });
    return spec;
  }
}
